"use client";

import { useEffect, useRef, useState } from "react";
import { useParams } from "react-router-dom";
import SignatureCanvas from "react-signature-canvas";
import axios from "axios";
import { toast } from "sonner";
import { Send, Undo } from "lucide-react";
import { VehiclePhotoCarousel } from "./VehiclePhotosCarousel";

function RemoteSignature() {
  const { token } = useParams();

  const [isLoading, setIsLoading] = useState(true);
  const [loadError, setLoadError] = useState("");
  const [inspection, setInspection] = useState(null);

  const [customerName, setCustomerName] = useState("");
  const [customerPhone, setCustomerPhone] = useState("");
  const [customerEmail, setCustomerEmail] = useState("");
  const [errors, setErrors] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [downloadLink, setDownloadLink] = useState(null);
  const [downloadLinkInvoice, setDownloadLinkInvoice] = useState(null);

  const sigCanvasRef = useRef(null);

  // Legenda de danos (mesmas opções do Inspection.jsx)
  const damageLegend = [
    { abbreviation: "B", label: "Bent" },
    { abbreviation: "BB", label: "Buffer Burned" },
    { abbreviation: "BR", label: "Broken" },
    { abbreviation: "C", label: "Cut" },
    { abbreviation: "CR", label: "Cracked" },
    { abbreviation: "D", label: "Dented" },
    { abbreviation: "L", label: "Loose" },
    { abbreviation: "M", label: "Missing" },
    { abbreviation: "PC", label: "Paint Chip" },
    { abbreviation: "R", label: "Rubbed" },
    { abbreviation: "S", label: "Scratched" },
    { abbreviation: "ST", label: "Stained" },
  ];

  const formatBoolean = (value) => {
    if (value === null || value === undefined) return "—";
    return value ? "Yes" : "No";
  };

  // Carrega dados da inspeção a partir do token
  useEffect(() => {
    const fetchInspection = async () => {
      if (!token) {
        setLoadError("Invalid link. Token not found.");
        setIsLoading(false);
        return;
      }

      try {
        const response = await axios.get(
          `https://new.cajuu.app/api/inspectionloaditem/remote/${token}`
        );

        const data = response.data;
        setInspection(data);

        const baseContact =
          data.inspection_type === "Pickup" ? data.pickup : data.delivery;

        if (baseContact) {
          setCustomerName(baseContact.customer_name || "");
          setCustomerPhone(baseContact.customer_phone || "");
          setCustomerEmail(baseContact.customer_email || "");
        }
      } catch (error) {
        console.error("Error loading remote inspection:", error);

        let message = "Unable to load inspection data.";
        if (error.response && error.response.data) {
          if (error.response.data.error) {
            message = error.response.data.error;
          } else if (error.response.data.details) {
            message = JSON.stringify(error.response.data.details);
          }
        }

        setLoadError(message);
      } finally {
        setIsLoading(false);
      }
    };

    fetchInspection();
  }, [token]);

  const clearSignature = () => {
    if (sigCanvasRef.current) {
      sigCanvasRef.current.clear();
    }
  };

  const validate = () => {
    const newErrors = {};

    if (!customerName.trim()) {
      newErrors.customerName = "Customer name is required.";
    }
    if (!customerPhone.trim()) {
      newErrors.customerPhone = "Customer phone is required.";
    }
    if (!customerEmail.trim()) {
      newErrors.customerEmail = "Customer email is required.";
    } else if (!/\S+@\S+\.\S+/.test(customerEmail.trim())) {
      newErrors.customerEmail = "Please enter a valid email address.";
    }

    if (!sigCanvasRef.current || sigCanvasRef.current.isEmpty()) {
      newErrors.signature = "Customer signature is required.";
    }

    setErrors(newErrors);
    return newErrors;
  };

  /**
   * Converte a assinatura desenhada (branca em fundo transparente)
   * para uma imagem PNG com os traços em PRETO, mantendo o fundo transparente.
   */
  const getBlackSignatureDataURL = () => {
    if (!sigCanvasRef.current) return null;

    const originalCanvas = sigCanvasRef.current.getTrimmedCanvas();
    const tmpCanvas = document.createElement("canvas");
    tmpCanvas.width = originalCanvas.width;
    tmpCanvas.height = originalCanvas.height;

    const ctx = tmpCanvas.getContext("2d");
    ctx.drawImage(originalCanvas, 0, 0);

    const imageData = ctx.getImageData(0, 0, tmpCanvas.width, tmpCanvas.height);
    const data = imageData.data;

    for (let i = 0; i < data.length; i += 4) {
      const alpha = data[i + 3];

      if (alpha !== 0) {
        data[i] = 0; // R
        data[i + 1] = 0; // G
        data[i + 2] = 0; // B
        data[i + 3] = 255; // A
      }
    }

    ctx.putImageData(imageData, 0, 0);

    return tmpCanvas.toDataURL("image/png");
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    if (submitting) return;

    const validationErrors = validate();
    if (Object.keys(validationErrors).length > 0) {
      toast.error("Please check the highlighted fields.");
      return;
    }

    try {
      setSubmitting(true);

      const formData = new FormData();
      formData.append("token", token);
      formData.append("customer_name", customerName.trim());
      formData.append("customer_phone", customerPhone.trim());
      formData.append("customer_email", customerEmail.trim());

      const dataUrl = getBlackSignatureDataURL();
      const blob = await fetch(dataUrl).then((res) => res.blob());
      formData.append("customer_signature", blob, "signature.png");

      const response = await axios.post(
        "https://new.cajuu.app/api/inspectionloaditem/remote-signature",
        formData,
        {
          headers: {
            "Content-Type": "multipart/form-data",
          },
        }
      );

      setSubmitted(true);
      setDownloadLink(response.data?.downloadLink || null);
      setDownloadLinkInvoice(response.data?.downloadLinkInvoice || null);

      toast.success("Signature submitted successfully!", {
        description: "Thank you for completing your remote inspection.",
        duration: 4000,
      });
    } catch (error) {
      console.error("Error submitting remote signature:", error);

      let errorMessage = "Request failed.";
      if (error.response && error.response.data) {
        const { message, error: errDetail, details } = error.response.data;
        if (message || errDetail) {
          errorMessage = `${message || ""} ${errDetail || ""}`.trim();
        } else if (details) {
          errorMessage = JSON.stringify(details);
        }
      }

      toast.error("Error submitting signature", {
        description: errorMessage,
        duration: 5000,
      });
    } finally {
      setSubmitting(false);
    }
  };

  if (isLoading) {
    return (
      <div className="min-h-screen bg-[#050505] flex items-center justify-center">
        <div className="flex flex-col items-center">
          <div className="w-24 h-24 rounded-full bg-white/5 flex items-center justify-center">
            <img
              src="https://view.cajuu.app/img/cajuu.png"
              alt="Cajuu logo"
              className="w-16 h-16 animate-bounce"
            />
          </div>
          <p className="mt-4 text-sm text-gray-300">
            Loading remote inspection...
          </p>
        </div>
      </div>
    );
  }

  const hasVehicles =
    inspection &&
    (Array.isArray(inspection.vehicles) ? inspection.vehicles.length > 0 : false);

  const hasLoadItems =
    inspection &&
    (Array.isArray(inspection.load_items) ? inspection.load_items.length > 0 : false);

  const shouldShowCarousel = !loadError && inspection && (hasVehicles || hasLoadItems);

  return (
    <div className="min-h-screen bg-[#050505] text-gray-100 flex items-center justify-center px-10 py-10">
      <div className="w-full bg-[#111111] border border-gray-800 rounded-2xl shadow-xl p-6 md:p-8 relative overflow-hidden">
        {/* Header */}
        <div className="flex items-center gap-3 mb-6">
          <div className="w-11 h-11 rounded-full bg-white/5 flex items-center justify-center">
            <img
              src="https://view.cajuu.app/img/cajuu.png"
              alt="Cajuu logo"
              className="w-8 h-8"
            />
          </div>
          <div>
            <h1 className="text-xl md:text-2xl font-semibold">
              Remote Vehicle Inspection
            </h1>
            {inspection?.vehicle && (
              <p className="text-xs md:text-sm text-gray-400 mt-1">
                {inspection.vehicle.year} {inspection.vehicle.make}{" "}
                {inspection.vehicle.model} · VIN {inspection.vehicle.vin}
              </p>
            )}
          </div>
        </div>

        {/* Mensagem de erro */}
        {loadError && (
          <div className="bg-red-500/10 border border-red-500/40 text-red-200 text-sm rounded-lg p-4">
            <p>{loadError}</p>
            <p className="mt-2 text-xs text-red-200/80">
              If you believe this is a mistake, please contact your driver or
              the transport company.
            </p>
          </div>
        )}

        {/* Já assinado */}
        {!loadError && inspection?.already_signed && !submitted && (
          <div className="bg-emerald-500/10 border border-emerald-500/40 text-emerald-200 text-sm rounded-lg p-4">
            <p>This inspection has already been signed.</p>
            <p className="mt-2 text-xs text-emerald-200/80">
              If you need any changes, please contact your driver or the
              transport company.
            </p>
          </div>
        )}

        {/* Form principal */}
        {!loadError && !submitted && inspection && !inspection.already_signed && (
          <form onSubmit={handleSubmit} className="space-y-6 mt-4">
            {/* Resumo da viagem */}
            <section className="text-xs md:text-sm text-gray-300 bg-white/5 border border-white/5 rounded-xl p-4 space-y-2">
              <p>
                Please review the information below and complete your details to
                sign this vehicle inspection. Your signature confirms the
                vehicle condition at pickup/delivery.
              </p>

              <div className="grid gap-3 md:grid-cols-2 pt-2 border-t border-white/5 mt-2">
                {inspection.pickup && (
                  <div>
                    <h2 className="text-xs font-semibold text-gray-100 mb-1">
                      Pickup
                    </h2>
                    <p className="text-xs text-gray-300">
                      {inspection.pickup.address}
                      <br />
                      {inspection.pickup.city}, {inspection.pickup.state}{" "}
                      {inspection.pickup.zipcode}
                    </p>
                  </div>
                )}

                {inspection.delivery && (
                  <div>
                    <h2 className="text-xs font-semibold text-gray-100 mb-1">
                      Delivery
                    </h2>
                    <p className="text-xs text-gray-300">
                      {inspection.delivery.address}
                      <br />
                      {inspection.delivery.city}, {inspection.delivery.state}{" "}
                      {inspection.delivery.zipcode}
                    </p>
                  </div>
                )}
              </div>
            </section>

            {/* Condição do veículo preenchida pelo driver */}
            <section className="space-y-3 text-xs md:text-sm text-gray-300 bg-white/5 border border-white/5 rounded-xl p-4">
              <h2 className="text-sm font-semibold text-gray-100">
                Vehicle condition recorded by driver
              </h2>
              <p className="text-[11px] text-gray-400">
                These details were recorded by the driver during the{" "}
                {inspection.inspection_type === "Pickup"
                  ? "pickup inspection."
                  : "delivery inspection."}
              </p>

              <div className="grid gap-3 md:grid-cols-3 mt-2">
                <div>
                  <p className="text-[11px] text-gray-400">Odometer</p>
                  <p className="text-xs text-gray-100">
                    {inspection.odometer !== null &&
                    inspection.odometer !== undefined
                      ? inspection.odometer
                      : "—"}
                  </p>
                </div>

                <div>
                  <p className="text-[11px] text-gray-400">Drivable</p>
                  <p className="text-xs text-gray-100">
                    {formatBoolean(inspection.drivable)}
                  </p>
                </div>

                <div>
                  <p className="text-[11px] text-gray-400">Windscreen</p>
                  <p className="text-xs text-gray-100">
                    {formatBoolean(inspection.windscreen)}
                  </p>
                </div>

                <div>
                  <p className="text-[11px] text-gray-400">Intact glass</p>
                  <p className="text-xs text-gray-100">
                    {formatBoolean(inspection.intact_glass)}
                  </p>
                </div>

                <div>
                  <p className="text-[11px] text-gray-400">Paperwork</p>
                  <p className="text-xs text-gray-100">
                    {formatBoolean(inspection.paperwork)}
                  </p>
                </div>

                <div>
                  <p className="text-[11px] text-gray-400">Keys</p>
                  <p className="text-xs text-gray-100">
                    {inspection.keys_qt !== null &&
                    inspection.keys_qt !== undefined
                      ? inspection.keys_qt
                      : "—"}
                  </p>
                </div>

                <div>
                  <p className="text-[11px] text-gray-400">Spare tire</p>
                  <p className="text-xs text-gray-100">
                    {formatBoolean(inspection.spare_tire)}
                  </p>
                </div>

                <div className="md:col-span-2">
                  <p className="text-[11px] text-gray-400">Other items</p>
                  <p className="text-xs text-gray-100">
                    {inspection.others && inspection.others.trim() !== ""
                      ? inspection.others
                      : "—"}
                  </p>
                </div>
              </div>

              {inspection.notes && inspection.notes.trim() !== "" && (
                <div className="pt-2 border-t border-white/5 mt-2">
                  <p className="text-[11px] text-gray-400">Driver notes</p>
                  <p className="text-xs text-gray-100 whitespace-pre-wrap">
                    {inspection.notes}
                  </p>
                </div>
              )}
            </section>

            {/* Dados do cliente */}
            <section className="space-y-4">
              <h2 className="text-sm font-semibold text-gray-100">
                Your information
              </h2>
              <div className="grid gap-4 md:grid-cols-3">
                <div className="flex flex-col gap-1">
                  <label
                    htmlFor="customerName"
                    className="text-xs text-gray-300"
                  >
                    Full name <span className="text-red-400">*</span>
                  </label>
                  <input
                    id="customerName"
                    type="text"
                    value={customerName}
                    onChange={(e) => setCustomerName(e.target.value)}
                    className={`w-full rounded-lg border px-3 py-2 text-sm bg-black/40 border-gray-700 focus:outline-none focus:ring-2 focus:ring-amber-500 focus:border-amber-500 ${
                      errors.customerName ? "border-red-500" : ""
                    }`}
                    autoComplete="name"
                  />
                  {errors.customerName && (
                    <p className="text-xs text-red-400">
                      {errors.customerName}
                    </p>
                  )}
                </div>

                <div className="flex flex-col gap-1">
                  <label
                    htmlFor="customerPhone"
                    className="text-xs text-gray-300"
                  >
                    Phone <span className="text-red-400">*</span>
                  </label>
                  <input
                    id="customerPhone"
                    type="tel"
                    value={customerPhone}
                    onChange={(e) => setCustomerPhone(e.target.value)}
                    className={`w-full rounded-lg border px-3 py-2 text-sm bg-black/40 border-gray-700 focus:outline-none focus:ring-2 focus:ring-amber-500 focus:border-amber-500 ${
                      errors.customerPhone ? "border-red-500" : ""
                    }`}
                    autoComplete="tel"
                  />
                  {errors.customerPhone && (
                    <p className="text-xs text-red-400">
                      {errors.customerPhone}
                    </p>
                  )}
                </div>

                <div className="flex flex-col gap-1">
                  <label
                    htmlFor="customerEmail"
                    className="text-xs text-gray-300"
                  >
                    Email <span className="text-red-400">*</span>
                  </label>
                  <input
                    id="customerEmail"
                    type="email"
                    value={customerEmail}
                    onChange={(e) => setCustomerEmail(e.target.value)}
                    className={`w-full rounded-lg border px-3 py-2 text-sm bg-black/40 border-gray-700 focus:outline-none focus:ring-2 focus:ring-amber-500 focus:border-amber-500 ${
                      errors.customerEmail ? "border-red-500" : ""
                    }`}
                    autoComplete="email"
                  />
                  {errors.customerEmail && (
                    <p className="text-xs text-red-400">
                      {errors.customerEmail}
                    </p>
                  )}
                </div>
              </div>
            </section>

            {/* Diagrama + legenda */}
            {inspection.diagram_url && (
              <section className="space-y-2">
                <h2 className="text-sm font-semibold text-gray-100">
                  Damage diagram
                </h2>
                <div className="rounded-xl overflow-hidden border border-gray-800 bg-black/60">
                  <img
                    src={inspection.diagram_url}
                    alt="Vehicle damage diagram"
                    className="w-full object-contain bg-black"
                  />
                </div>

                <div className="mt-3 rounded-xl bg-black/40 border border-gray-800 p-3">
                  <h3 className="text-xs font-semibold text-gray-200">
                    Damage codes legend
                  </h3>
                  <div className="mt-2 grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-2 text-[11px] text-gray-300">
                    {damageLegend.map((item) => (
                      <div
                        key={item.abbreviation}
                        className="flex items-center gap-2"
                      >
                        <span className="inline-flex items-center justify-center w-6 h-6 rounded-full bg-amber-500 text-black text-xs font-bold">
                          {item.abbreviation}
                        </span>
                        <span>{item.label}</span>
                      </div>
                    ))}
                  </div>
                </div>
              </section>
            )}

            {/* ✅ Carrossel de fotos (SEM ALTERAR O COMPONENTE) */}
            {shouldShowCarousel && (
              <section className="mt-6 bg-white/5 border border-white/5 rounded-xl p-4">
                <VehiclePhotoCarousel selectedLoad={inspection} />
              </section>
            )}

            {/* Assinatura */}
            <section className="space-y-3">
              <div className="flex items-center justify-between gap-3">
                <h2 className="text-sm font-semibold text-gray-100">
                  Customer signature
                </h2>
                <button
                  type="button"
                  onClick={clearSignature}
                  className="inline-flex items-center gap-1 text-xs text-gray-300 hover:text-white transition-colors"
                >
                  <Undo size={14} />
                  Clear
                </button>
              </div>

              <div>
                <SignatureCanvas
                  ref={sigCanvasRef}
                  penColor="white"
                  canvasProps={{
                    height: 180,
                    className: `w-full border-dashed border-2 rounded-xl bg-[#1c1b19] ${
                      errors.signature ? "border-red-500" : "border-gray-700"
                    }`,
                  }}
                />
                {errors.signature && (
                  <p className="text-xs text-red-400 mt-1">
                    {errors.signature}
                  </p>
                )}
                <p className="mt-2 text-[11px] text-gray-400">
                  By signing above, you confirm the condition of the vehicle at{" "}
                  {inspection.inspection_type === "Pickup"
                    ? "pickup"
                    : "delivery"}
                  .
                </p>
              </div>
            </section>

            {/* Botão de envio */}
            <div className="pt-2 flex justify-end">
              <button
                type="submit"
                disabled={submitting}
                className={`inline-flex items-center justify-center gap-2 rounded-lg px-5 py-2.5 text-sm font-semibold text-black transition-all ${
                  submitting
                    ? "bg-amber-400 cursor-not-allowed"
                    : "bg-amber-500 hover:bg-amber-600 hover:scale-[0.98]"
                }`}
              >
                {submitting ? (
                  "Submitting..."
                ) : (
                  <>
                    <Send size={16} />
                    Submit signature
                  </>
                )}
              </button>
            </div>
          </form>
        )}

        {/* Tela de sucesso */}
        {!loadError && submitted && (
          <div className="space-y-4 mt-4">
            <div className="bg-emerald-500/10 border border-emerald-500/40 text-emerald-100 rounded-xl p-4">
              <h2 className="text-sm font-semibold">Thank you!</h2>
              <p className="text-xs mt-1">
                Your remote inspection has been completed and your signature has
                been saved successfully.
              </p>
            </div>

            {(downloadLink || downloadLinkInvoice) && (
              <div className="space-y-2 text-xs">
                {downloadLink && (
                  <p>
                    <a
                      href={downloadLink}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-amber-400 hover:text-amber-300 underline"
                    >
                      Open Bill of Lading (BOL)
                    </a>
                  </p>
                )}
                {downloadLinkInvoice && (
                  <p>
                    <a
                      href={downloadLinkInvoice}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-amber-400 hover:text-amber-300 underline"
                    >
                      Open Invoice
                    </a>
                  </p>
                )}
              </div>
            )}

            <p className="text-[11px] text-gray-400">
              You can now safely close this page.
            </p>
          </div>
        )}
      </div>
    </div>
  );
}

export default RemoteSignature;
